import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { apiClient } from '@/api/client'
import { qk } from '@/api/queryKeys'
import { useWorkspaceStore } from '@/stores/workspace'
import type { NotificationsListResult } from './notification'

// A short two-tone chime via Web Audio (no asset, no autoplay-blocked <audio>).
// The AudioContext resumes fine because it's created after the operator has
// already interacted with the page.
function playChime() {
  try {
    const AC =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!AC) return
    const ctx = new AC()
    const beep = (freq: number, start: number, dur: number) => {
      const o = ctx.createOscillator()
      const g = ctx.createGain()
      o.connect(g)
      g.connect(ctx.destination)
      o.type = 'sine'
      o.frequency.value = freq
      const t0 = ctx.currentTime + start
      g.gain.setValueAtTime(0.0001, t0)
      g.gain.exponentialRampToValueAtTime(0.22, t0 + 0.02)
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur)
      o.start(t0)
      o.stop(t0 + dur + 0.02)
    }
    beep(660, 0, 0.18)
    beep(880, 0.16, 0.28)
    window.setTimeout(() => ctx.close(), 800)
  } catch {
    // audio unavailable — silently ignore
  }
}

/**
 * useNotificationAlerts — global. Watches the operator's notifications and, when
 * a NEW one arrives (a gate needing confirmation: озвучка/монтаж/платні, or any
 * other), pops a corner toast + plays a chime. Polls as a safety net on top of
 * the SSE-driven refetch. Baselines on first load so old notifications don't fire.
 */
export function useNotificationAlerts(): void {
  const wsId = useWorkspaceStore((s) => s.activeWsId)
  const seenRef = useRef<number | null>(null)

  const q = useQuery<NotificationsListResult>({
    queryKey: [...qk.ws(wsId).notifications.list(), { alerts: true }] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<NotificationsListResult>(
        '/notifications?unread=true&page_size=10',
      )
      return data
    },
    refetchInterval: 15000,
    refetchOnWindowFocus: true,
  })

  // Reset the baseline when the active workspace changes (different notif stream).
  useEffect(() => {
    seenRef.current = null
  }, [wsId])

  useEffect(() => {
    const items = q.data?.items ?? []
    if (items.length === 0) return
    const maxId = Math.max(...items.map((i) => i.id))
    if (seenRef.current === null) {
      seenRef.current = maxId // first load → baseline, don't toast the backlog
      return
    }
    const lastSeen = seenRef.current
    // Alert on gates / important events; skip the noisy per-task 'video ready'
    // spam (those already show on the bell) so the chime means "you need to act".
    const fresh = items
      .filter((i) => i.id > lastSeen && i.type !== 'task_done' && i.type !== 'task_error')
      .sort((a, b) => a.id - b.id)
    seenRef.current = maxId
    if (fresh.length === 0) return
    for (const n of fresh) {
      toast.warning(n.title, {
        description: n.body ?? undefined,
        duration: 10000,
      })
    }
    playChime()
  }, [q.data])
}
